/**
 * Kunde anlegen oder bearbeiten.
 *
 * Pflichtfelder sind Nachname bzw. Name der Organisation, Ort und Kunden-Nr.
 * Die Dublettenprüfung läuft live gegen den Server und nennt den Grund des
 * Treffers – so entsteht kein zweiter Datensatz für denselben Haushalt.
 *
 * Beim Anlegen sucht das Namensfeld mit – dieselbe Liste wie in der
 * Angebots-Anlage. Wer einen Bestandskunden wählt, bearbeitet ihn, statt ihn
 * ein zweites Mal anzulegen.
 */

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiFehler } from '../api';
import { useMeta } from '../zustand';
import { Modal } from '../komponenten/Modal';
import { mailPlausibel } from '../hilfen/format';
import { Kundenvorschlaege, useKundensuche } from '../komponenten/Kundensuche';
import type { Kunde, KundenTreffer } from '../typen';
import './kundenmodal.css';

type Formular = {
  typ: 'person' | 'organisation';
  kundenNr: string;
  rolle: string;
  anrede: string;
  titel: string;
  vorname: string;
  nachname: string;
  firma: string;
  ansprech: string;
  position: string;
  namenszusatz: string;
  strasse: string;
  plz: string;
  ort: string;
  land: string;
  montage: string;
  telefon: string;
  mobil: string;
  mobilGeschaeftlich: string;
  mail: string;
  web: string;
  uid: string;
  herkunft: string;
  tags: string[];
  notiz: string;
};

const LEER: Formular = {
  typ: 'person',
  kundenNr: '',
  rolle: 'Kunde',
  anrede: '',
  titel: '',
  vorname: '',
  nachname: '',
  firma: '',
  ansprech: '',
  position: '',
  namenszusatz: '',
  strasse: '',
  plz: '',
  ort: '',
  land: 'Österreich',
  montage: '',
  telefon: '',
  mobil: '',
  mobilGeschaeftlich: '',
  mail: '',
  web: '',
  uid: '',
  herkunft: '',
  tags: [],
  notiz: '',
};

/** Bestehenden Kunden in die Maske übertragen. */
function ausKunde(k: Kunde): Formular {
  return {
    typ: k.typ,
    kundenNr: k.kundenNr,
    rolle: k.rolle,
    anrede: k.anrede,
    titel: k.titel,
    vorname: k.vorname,
    nachname: k.nachname,
    firma: k.firma,
    ansprech: k.ansprech,
    position: k.position,
    namenszusatz: k.namenszusatz,
    strasse: k.strasse,
    plz: k.plz,
    ort: k.ort,
    land: k.land,
    montage: k.montage,
    telefon: k.telefon,
    mobil: k.mobil,
    mobilGeschaeftlich: k.mobilGeschaeftlich,
    mail: k.mail,
    web: k.web,
    uid: k.uid,
    herkunft: k.herkunft,
    tags: [...k.tags],
    notiz: k.notiz,
  };
}

export function KundenModal({
  kundeId,
  vorbelegung,
  onSchliessen,
  onGespeichert,
}: {
  kundeId: number | null;
  /**
   * Felder, die beim Öffnen schon gefüllt sein sollen.
   *
   * Kommt aus der Kundensuche: Wer „Martin Zihr" getippt und dann „Neuen
   * Kunden anlegen" gewählt hat, will den Namen nicht ein zweites Mal tippen.
   */
  vorbelegung?: Partial<Formular>;
  onSchliessen: () => void;
  onGespeichert?: (kunde: Kunde) => void;
}) {
  const meta = useMeta();
  const queryClient = useQueryClient();

  /**
   * Welchen Datensatz bearbeitet die Maske gerade? Startet mit dem übergebenen
   * Kunden – wechselt aber auf einen Bestandskunden, wenn die Namenssuche
   * einen liefert. Genau das verhindert die Dublette schon vor dem Speichern.
   */
  const [id, setId] = useState<number | null>(kundeId);
  const neu = id === null;

  const [f, setF] = useState<Formular>(LEER);
  const [tab, setTab] = useState<'adresse' | 'kontakt' | 'weiteres'>('adresse');
  const [fehler, setFehler] = useState('');
  const [bereit, setBereit] = useState(false);

  const kundenAbfrage = useQuery({
    queryKey: ['kunden'],
    queryFn: () => api.hole<Kunde[]>('/kunden'),
  });

  const nummerAbfrage = useQuery({
    queryKey: ['naechste-kundennummer'],
    queryFn: () => api.hole<{ kundenNr: string }>('/kunden/naechste-nummer'),
    enabled: neu,
  });

  // Formular einmalig füllen: bestehender Kunde oder Vorschlagsnummer.
  useEffect(() => {
    if (bereit) return;
    if (neu) {
      if (!nummerAbfrage.data) return;
      setF({ ...LEER, ...vorbelegung, kundenNr: nummerAbfrage.data.kundenNr });
      setBereit(true);
      return;
    }
    const k = kundenAbfrage.data?.find((x) => x.id === id);
    if (!k) return;
    setF(ausKunde(k));
    setBereit(true);
    // vorbelegung bewusst nicht in den Abhängigkeiten: Sie gilt beim Öffnen.
    // Später soll ein Tippfehler im Suchfeld nicht die Eingaben überschreiben.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bereit, neu, nummerAbfrage.data, kundenAbfrage.data, id]);

  // Dublettenprüfung, verzögert damit nicht jede Taste eine Anfrage auslöst.
  const [dubletten, setDubletten] = useState<KundenTreffer[]>([]);
  useEffect(() => {
    if (!bereit) return;
    const zeitgeber = window.setTimeout(() => {
      void api
        .sende<KundenTreffer[]>('/kunden/dubletten', {
          id,
          typ: f.typ,
          anrede: f.anrede,
          titel: f.titel,
          vorname: f.vorname,
          nachname: f.nachname,
          firma: f.firma,
          ort: f.ort,
          mail: f.mail,
          telefon: f.telefon,
          mobil: f.mobil,
          mobilGeschaeftlich: f.mobilGeschaeftlich,
          strasse: f.strasse,
        })
        .then(setDubletten)
        .catch(() => setDubletten([]));
    }, 400);
    return () => window.clearTimeout(zeitgeber);
    // Ort und Mobil gehören dazu, seit der Server auf „Name und Ort" prüft –
    // sonst zeigt die Live-Prüfung weniger an, als beim Speichern blockiert.
  }, [
    bereit,
    id,
    f.typ,
    f.anrede,
    f.titel,
    f.vorname,
    f.nachname,
    f.firma,
    f.ort,
    f.mail,
    f.telefon,
    f.mobil,
    f.mobilGeschaeftlich,
    f.strasse,
  ]);

  /**
   * Was fehlt noch?
   *
   * Bei Privatpersonen ist der Datensatz nur vollständig, wenn man den Kunden
   * auch erreichen und anschreiben kann – deshalb gehören Adresse, Mail und
   * Mobilnummer dazu. Bei Organisationen bleibt es beim Namen, weil dort oft
   * nur eine Zentrale bekannt ist, wenn der Vorgang entsteht.
   *
   * Geprüft wird hier, nicht am Server: Die aus Monday übernommenen Kunden
   * haben teils weder Mail noch Mobilnummer, und die Angebots-Anlage legt
   * Kunden aus wenigen Feldern an. Eine Serverpflicht würde beides brechen.
   */
  const fehlend = useMemo(() => {
    const leer = (w: string) => !w.trim();
    const luecken: string[] = [];

    if (f.typ === 'organisation') {
      if (leer(f.firma)) luecken.push('Name der Organisation');
      if (leer(f.ort)) luecken.push('Ort');
    } else {
      if (leer(f.vorname)) luecken.push('Vorname');
      if (leer(f.nachname)) luecken.push('Nachname');
      if (leer(f.strasse)) luecken.push('Straße');
      if (leer(f.plz)) luecken.push('PLZ');
      if (leer(f.ort)) luecken.push('Ort');
      if (leer(f.mail)) luecken.push('E-Mail');
      else if (!mailPlausibel(f.mail)) luecken.push('gültige E-Mail');
      // Eine der beiden Mobilnummern genügt - welche, entscheidet der Kunde.
      if (leer(f.mobil) && leer(f.mobilGeschaeftlich)) luecken.push('Mobilnummer');
    }

    if (leer(f.kundenNr)) luecken.push('Kunden-Nr.');
    return luecken;
  }, [f]);

  const gueltig = fehlend.length === 0;
  /** Sternchen nur dort, wo das Feld für diesen Kundentyp wirklich Pflicht ist. */
  const Pflicht = () => (f.typ === 'person' ? <span className="km-pflicht">*</span> : null);

  /**
   * Vom Server geblockte Dubletten. Solange diese Liste steht, wartet die
   * Maske auf eine Entscheidung: bestehenden Kunden nehmen oder bewusst
   * trotzdem anlegen.
   */
  const [geblockt, setGeblockt] = useState<KundenTreffer[]>([]);

  const speichern = useMutation({
    mutationFn: ({ undNeu, trotzdem }: { undNeu: boolean; trotzdem?: boolean }) => {
      const pfad = neu ? '/kunden' : `/kunden/${id}`;
      const koerper = trotzdem ? { ...f, trotzdem: true } : f;
      const aufruf = neu
        ? api.sende<Kunde>(pfad, koerper)
        : api.ersetze<Kunde>(pfad, koerper);
      return aufruf.then((k) => ({ kunde: k, undNeu }));
    },
    onSuccess: ({ kunde, undNeu }) => {
      void queryClient.invalidateQueries({ queryKey: ['kunden'] });
      void queryClient.invalidateQueries({ queryKey: ['angebote'] });
      setGeblockt([]);
      onGespeichert?.(kunde);
      if (undNeu) {
        setF({ ...LEER, kundenNr: String(Number(f.kundenNr.replace(/\D/g, '')) + 1) });
        setTab('adresse');
        setDubletten([]);
      } else {
        onSchliessen();
      }
    },
    onError: (fe) => {
      // 409 mit Dublettenliste ist keine Fehlermeldung, sondern eine Rückfrage.
      const treffer =
        fe instanceof ApiFehler && fe.status === 409
          ? ((fe.daten as { dubletten?: KundenTreffer[] } | null)?.dubletten ?? [])
          : [];
      if (treffer.length > 0) {
        setGeblockt(treffer);
        setFehler('');
        return;
      }
      setGeblockt([]);
      setFehler(fe instanceof Error ? fe.message : 'Speichern fehlgeschlagen.');
    },
  });

  function setzen<K extends keyof Formular>(feld: K, wert: Formular[K]) {
    setF((alt) => ({ ...alt, [feld]: wert }));
    setFehler('');
  }

  // Namenssuche – dieselbe Komponente wie in der Angebots-Anlage. Sie ruht,
  // sobald ein bestehender Kunde in der Maske liegt.
  const suchname = f.typ === 'organisation' ? f.firma : f.nachname;
  const suche = useKundensuche(suchname, !neu || !bereit);

  /** Name tippen und dabei suchen lassen. */
  function nameTippen(feld: 'nachname' | 'firma', wert: string) {
    setzen(feld, wert);
    suche.oeffnen();
  }

  /**
   * Bestandskunden übernehmen: die Maske wechselt vom Anlegen ins Bearbeiten.
   * So kann die Dublette gar nicht erst entstehen.
   */
  function kundeUebernehmen(k: Kunde) {
    setId(k.id);
    setF(ausKunde(k));
    setDubletten([]);
    setGeblockt([]);
    setFehler('');
    suche.schliessen();
  }

  /** Doch ein neuer Kunde – zurück auf ein leeres Formular. */
  function loesen() {
    setId(null);
    setF({ ...LEER, ...vorbelegung, kundenNr: nummerAbfrage.data?.kundenNr ?? '' });
    setDubletten([]);
    setGeblockt([]);
    setFehler('');
    setTab('adresse');
    suche.oeffnen();
  }

  /** Wurde ein Bestandskunde aus der Suche geholt? Dann ist ein „Lösen" fällig. */
  const uebernommen = kundeId === null && id !== null;

  const anzeigeName =
    f.typ === 'organisation'
      ? f.firma || 'Neue Organisation'
      : [f.anrede === 'Familie' ? 'Familie' : '', f.titel, f.vorname, f.nachname]
          .filter(Boolean)
          .join(' ') || 'Neuer Kontakt';

  const hinweis = gueltig
    ? neu
      ? 'Wird sofort in der Angebots-Anlage als Bestandskunde gefunden.'
      : `Kunden-Nr. ${f.kundenNr} · ${f.rolle}`
    : `Es fehlt noch: ${fehlend.join(', ')}`;

  return (
    <Modal
      titel={neu ? 'Neuen Kunden anlegen' : 'Kontaktdaten bearbeiten'}
      untertitel={
        neu
          ? 'Privatperson oder Organisation – Pflicht sind Name, Ort und Kunden-Nr.'
          : `Änderungen gelten für alle Angebote von ${anzeigeName}.`
      }
      breite={920}
      abstandOben={40}
      zeigeSchliessen
      fest={speichern.isPending}
      onSchliessen={onSchliessen}
      fuss={
        <div className="km-fuss">
          <div className="km-fuss-hinweis">{fehler || hinweis}</div>
          <div className="km-fuss-knoepfe">
            <button
              type="button"
              className="km-knopf-sek"
              onClick={onSchliessen}
              disabled={speichern.isPending}
            >
              Abbrechen
            </button>
            {neu && (
              <button
                type="button"
                className="km-knopf-sek"
                disabled={!gueltig || speichern.isPending}
                onClick={() => speichern.mutate({ undNeu: true })}
              >
                Speichern und neu
              </button>
            )}
            <button
              type="button"
              className={`km-knopf-prim ${gueltig ? '' : 'gesperrt'}`}
              disabled={!gueltig || speichern.isPending}
              onClick={() => speichern.mutate({ undNeu: false })}
            >
              {neu ? 'Kunde anlegen' : 'Änderungen speichern'}
            </button>
          </div>
        </div>
      }
    >
      {uebernommen && (
        <div className="km-uebernommen">
          <div>
            <strong>Bestandskunde geöffnet:</strong> {anzeigeName} · Kunden-Nr. {f.kundenNr}
            <div className="km-uebernommen-zeile">
              Änderungen gelten für alle Angebote dieses Kunden – es entsteht kein zweiter
              Datensatz.
            </div>
          </div>
          <button type="button" className="km-knopf-sek" onClick={loesen}>
            Doch neu anlegen
          </button>
        </div>
      )}

      <div className="km-typwahl">
        <button
          type="button"
          className={f.typ === 'person' ? 'aktiv' : ''}
          onClick={() => setzen('typ', 'person')}
        >
          Privatperson
        </button>
        <button
          type="button"
          className={f.typ === 'organisation' ? 'aktiv' : ''}
          onClick={() => setzen('typ', 'organisation')}
        >
          Organisation
        </button>
      </div>

      <div className="km-oben">
        <div className="km-namensfelder">
          {f.typ === 'person' ? (
            <>
              <div className="km-raster km-raster--anrede">
                <label className="km-feld">
                  <span>Anrede</span>
                  <select
                    className="km-eingabe"
                    value={f.anrede}
                    onChange={(e) => setzen('anrede', e.target.value)}
                  >
                    {meta.anreden.map((a) => (
                      <option key={a.wert} value={a.wert}>
                        {a.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="km-feld">
                  <span>Titel</span>
                  <input
                    className="km-eingabe"
                    value={f.titel}
                    placeholder="z. B. Ing., Dr."
                    onChange={(e) => setzen('titel', e.target.value)}
                  />
                </label>
              </div>
              <div className="km-raster km-raster--zwei ks-feld">
                <label className="km-feld">
                  <span>
                    Vorname <Pflicht />
                  </span>
                  <input
                    className="km-eingabe"
                    value={f.vorname}
                    placeholder="Vorname"
                    onChange={(e) => setzen('vorname', e.target.value)}
                  />
                </label>
                <label className="km-feld">
                  <span>
                    Nachname <span className="km-pflicht">*</span>
                  </span>
                  <input
                    className="km-eingabe"
                    value={f.nachname}
                    placeholder={neu ? 'Tippen – sucht im Kundenstamm' : 'Nachname'}
                    role="combobox"
                    aria-expanded={suche.offen}
                    aria-autocomplete="list"
                    onChange={(e) => nameTippen('nachname', e.target.value)}
                    onKeyDown={(e) => suche.taste(e, kundeUebernehmen, suche.schliessen)}
                  />
                </label>
                {/*
                  Dieselbe Liste wie in der Angebots-Anlage – bewusst dieselbe Komponente,
                  damit sich beide Wege gleich anfühlen.
                */}
                <Kundenvorschlaege
                  suche={suchname}
                  suchstand={suche}
                  onWaehlen={kundeUebernehmen}
                  neuAktion={{
                    label: 'Als neuen Kunden anlegen',
                    hinweis: 'Formular unten weiter ausfüllen',
                    onAuswahl: suche.schliessen,
                  }}
                />
              </div>
            </>
          ) : (
            <>
              <label className="km-feld ks-feld">
                <span>
                  Name der Organisation <span className="km-pflicht">*</span>
                </span>
                <input
                  className="km-eingabe"
                  value={f.firma}
                  placeholder={neu ? 'Tippen – sucht im Kundenstamm' : 'z. B. Bauträger Novak GmbH'}
                  role="combobox"
                  aria-expanded={suche.offen}
                  aria-autocomplete="list"
                  onChange={(e) => nameTippen('firma', e.target.value)}
                  onKeyDown={(e) => suche.taste(e, kundeUebernehmen, suche.schliessen)}
                />
                {/*
                  Dieselbe Liste wie in der Angebots-Anlage – bewusst dieselbe Komponente,
                  damit sich beide Wege gleich anfühlen.
                */}
                <Kundenvorschlaege
                  suche={suchname}
                  suchstand={suche}
                  onWaehlen={kundeUebernehmen}
                  neuAktion={{
                    label: 'Als neuen Kunden anlegen',
                    hinweis: 'Formular unten weiter ausfüllen',
                    onAuswahl: suche.schliessen,
                  }}
                />
              </label>
              <div className="km-raster km-raster--zwei">
                <label className="km-feld">
                  <span>Ansprechpartner</span>
                  <input
                    className="km-eingabe"
                    value={f.ansprech}
                    placeholder="Vor- und Nachname"
                    onChange={(e) => setzen('ansprech', e.target.value)}
                  />
                </label>
                <label className="km-feld">
                  <span>Position</span>
                  <input
                    className="km-eingabe"
                    value={f.position}
                    placeholder="z. B. Bauleitung"
                    onChange={(e) => setzen('position', e.target.value)}
                  />
                </label>
              </div>
            </>
          )}

          <label className="km-feld">
            <span>Namenszusatz</span>
            <input
              className="km-eingabe"
              value={f.namenszusatz}
              placeholder={f.typ === 'organisation' ? 'z. B. Bauteil, Projektname' : 'z. B. c/o, Stiege 2'}
              onChange={(e) => setzen('namenszusatz', e.target.value)}
            />
          </label>
        </div>

        <div className="km-nummernblock">
          <label className="km-feld">
            <span>
              Kunden-Nr. <span className="km-pflicht">*</span>
            </span>
            <input
              className="km-eingabe km-zahl"
              value={f.kundenNr}
              onChange={(e) => setzen('kundenNr', e.target.value)}
            />
          </label>
          <label className="km-feld">
            <span>Typ</span>
            <select
              className="km-eingabe"
              value={f.rolle}
              onChange={(e) => setzen('rolle', e.target.value)}
            >
              {meta.kundenTypen.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </label>
          <div className="km-nummern-hinweis">
            {neu
              ? 'Vorschlag aus der laufenden Nummerierung – überschreibbar.'
              : 'Kunden-Nr. bleibt bei Änderungen erhalten.'}
          </div>
        </div>
      </div>

      <div className="km-tabs">
        {(
          [
            { k: 'adresse', l: 'Adresse' },
            { k: 'kontakt', l: 'Kontaktdetails' },
            { k: 'weiteres', l: 'Weiteres' },
          ] as const
        ).map((t) => (
          <button
            key={t.k}
            type="button"
            className={`km-tab ${tab === t.k ? 'aktiv' : ''}`}
            onClick={() => setTab(t.k)}
          >
            {t.l}
          </button>
        ))}
      </div>

      {tab === 'adresse' && (
        <div className="km-raster km-raster--adresse">
          <label className="km-feld km-voll">
            <span>
              Straße und Nummer <Pflicht />
            </span>
            <input
              className="km-eingabe"
              value={f.strasse}
              placeholder="z. B. Hauptstraße 14"
              onChange={(e) => setzen('strasse', e.target.value)}
            />
          </label>
          <label className="km-feld">
            <span>
              PLZ <Pflicht />
            </span>
            <input
              className="km-eingabe km-zahl"
              value={f.plz}
              placeholder="1010"
              onChange={(e) => setzen('plz', e.target.value)}
            />
          </label>
          <label className="km-feld">
            <span>
              Ort <span className="km-pflicht">*</span>
            </span>
            <input
              className="km-eingabe"
              value={f.ort}
              placeholder="Wien"
              onChange={(e) => setzen('ort', e.target.value)}
            />
          </label>
          <label className="km-feld">
            <span>Land</span>
            <select
              className="km-eingabe"
              value={f.land}
              onChange={(e) => setzen('land', e.target.value)}
            >
              {meta.laender.map((l) => (
                <option key={l} value={l}>
                  {l}
                </option>
              ))}
            </select>
          </label>
          <label className="km-feld">
            <span>Abweichende Montageadresse</span>
            <input
              className="km-eingabe"
              value={f.montage}
              placeholder="optional – Baustelle"
              onChange={(e) => setzen('montage', e.target.value)}
            />
          </label>
        </div>
      )}

      {tab === 'kontakt' && (
        <div className="km-raster km-raster--zwei">
          <label className="km-feld">
            <span>
              Mobil privat <Pflicht />
            </span>
            <input
              className="km-eingabe"
              value={f.mobil}
              placeholder="+43 664 …"
              onChange={(e) => setzen('mobil', e.target.value)}
            />
          </label>
          <label className="km-feld">
            <span>Mobil geschäftlich</span>
            <input
              className="km-eingabe"
              value={f.mobilGeschaeftlich}
              placeholder="+43 664 …"
              onChange={(e) => setzen('mobilGeschaeftlich', e.target.value)}
            />
          </label>
          <label className="km-feld">
            <span>
              E-Mail <Pflicht />
            </span>
            <input
              className="km-eingabe"
              value={f.mail}
              placeholder="name@domain.at"
              onChange={(e) => setzen('mail', e.target.value)}
            />
          </label>
          <label className="km-feld">
            <span>Webseite</span>
            <input
              className="km-eingabe"
              value={f.web}
              placeholder="www…"
              onChange={(e) => setzen('web', e.target.value)}
            />
          </label>
        </div>
      )}

      {tab === 'weiteres' && (
        <div className="km-weiteres">
          <div className="km-raster km-raster--zwei">
            <label className="km-feld">
              <span>UID / ATU-Nummer</span>
              <input
                className="km-eingabe"
                value={f.uid}
                placeholder={f.typ === 'organisation' ? 'ATU…' : 'nur bei Unternehmen'}
                onChange={(e) => setzen('uid', e.target.value)}
              />
            </label>
            <label className="km-feld">
              {/*
                Festnetz wird nicht mehr erfasst, aber nicht gelöscht: Bei den
                aus Monday übernommenen Kunden ist es teils die einzige Nummer,
                und die Dublettenprüfung vergleicht sie mit.
              */}
              <span>Telefon (Festnetz)</span>
              <input
                className="km-eingabe"
                value={f.telefon}
                placeholder="optional"
                onChange={(e) => setzen('telefon', e.target.value)}
              />
            </label>
            <label className="km-feld">
              <span>Herkunft / Empfehlung</span>
              <input
                className="km-eingabe"
                value={f.herkunft}
                placeholder="z. B. Architekt, Messe, Bestandskunde"
                onChange={(e) => setzen('herkunft', e.target.value)}
              />
            </label>
          </div>

          <div className="km-feld">
            <span>Merkmale</span>
            <div className="eh-chips">
              {meta.tagListe.map((t) => {
                const an = f.tags.includes(t);
                return (
                  <button
                    key={t}
                    type="button"
                    className={`eh-chip ${an ? 'eh-chip--aktiv' : ''}`}
                    onClick={() =>
                      setzen('tags', an ? f.tags.filter((x) => x !== t) : [...f.tags, t])
                    }
                  >
                    {t}
                  </button>
                );
              })}
            </div>
          </div>

          <label className="km-feld">
            <span>Interne Notiz</span>
            <textarea
              className="km-eingabe km-textarea"
              rows={3}
              value={f.notiz}
              placeholder="Was das Team wissen sollte …"
              onChange={(e) => setzen('notiz', e.target.value)}
            />
          </label>
        </div>
      )}

      {dubletten.length > 0 && (
        <div className="km-dubletten">
          <div className="km-dubletten-kopf">
            <span className="km-dubletten-punkt" />
            Es gibt bereits einen ähnlichen Kontakt
          </div>
          <div className="km-dubletten-liste">
            {dubletten.map((t) => (
              <div className="km-dublette" key={t.kunde.id}>
                <div>
                  <div className="km-dublette-name">{t.kunde.name}</div>
                  <div className="km-dublette-zeile">
                    {t.kunde.adresse}, {t.kunde.ortZeile} · {t.kunde.mail || '—'}
                  </div>
                  <div className="km-dublette-grund">Übereinstimmung: {t.grund}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="km-dubletten-hinweis">
            Bitte prüfen, ob der Kontakt schon existiert – Angebote lassen sich einem bestehenden
            Kunden zuordnen.
          </div>
        </div>
      )}

      {/*
        Vom Server geblockt: hier ist die Übereinstimmung so eindeutig, dass
        Speichern zur Entscheidung wird. Der Weg „trotzdem" bleibt offen, denn
        es gibt echte Fälle – Vater und Sohn an derselben Adresse, zwei Firmen
        mit derselben Telefonzentrale.
      */}
      {geblockt.length > 0 && (
        <div className="km-geblockt">
          <div className="km-geblockt-kopf">
            {geblockt.length === 1
              ? 'Diesen Kunden gibt es bereits'
              : `${geblockt.length} passende Kunden gibt es bereits`}
          </div>

          <div className="km-dubletten-liste">
            {geblockt.map((t) => (
              <div className="km-dublette" key={t.kunde.id}>
                <div>
                  <div className="km-dublette-name">{t.kunde.name}</div>
                  <div className="km-dublette-zeile">
                    Kunden-Nr. {t.kunde.kundenNr} · {t.kunde.adresse}, {t.kunde.ortZeile} ·{' '}
                    {t.kunde.mail || 'keine E-Mail'}
                  </div>
                  <div className="km-dublette-grund">Übereinstimmung: {t.grund}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="km-geblockt-knoepfe">
            <button
              type="button"
              className="km-knopf-sek"
              onClick={() => setGeblockt([])}
              disabled={speichern.isPending}
            >
              Zurück zur Eingabe
            </button>
            <button
              type="button"
              className="km-knopf-trotzdem"
              onClick={() => speichern.mutate({ undNeu: false, trotzdem: true })}
              disabled={speichern.isPending}
            >
              {speichern.isPending ? 'Wird gespeichert …' : 'Trotzdem als eigenen Kunden anlegen'}
            </button>
          </div>

          <div className="km-dubletten-hinweis">
            Ein Angebot lässt sich dem bestehenden Kunden zuordnen – dafür dieses Fenster schließen
            und in der Angebots-Anlage nach dem Namen suchen.
          </div>
        </div>
      )}
    </Modal>
  );
}
